import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { VpnKey } from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { EditPage, FuseInput } from '/src/components';
import { useUserStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { ChangePasswordForm, DataModelType, FormErrors } from '/src/lib/types';

/**
 * Edit User Page
 */
export const ChangePasswordPage: React.FC = observer(() => {
  /** Context **/
  const userStore = useUserStore();

  /** State **/
  const [formValues, setFormValues] = useState<ChangePasswordForm>({
    currentPassword: '',
    newPassword: '',
    verifyPassword: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    if (formValues) {
      setFormValues({
        ...formValues,
        [key]: value,
      });
      // Clear error when field is updated
      if (formErrors[key]) {
        setFormErrors({
          ...formErrors,
          [key]: '',
        });
      }
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formValues?.currentPassword) errors.currentPassword = 'Current password is required';
    if (!formValues?.newPassword) errors.newPassword = 'New password is required';
    if (!formValues?.verifyPassword) errors.verifyPassword = 'Verify password is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Change the User's password.
   */
  const changeUserPassword = async () => {
    if (!formValues || !userStore?.user) return;

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    try {
      await userStore.changeUserPassword(formValues.currentPassword, formValues.newPassword, formValues.verifyPassword);

      toast.success(toastMessages.changePassword.success);

      setFormValues({
        currentPassword: '',
        newPassword: '',
        verifyPassword: '',
      });
    } catch (err) {
      toast.success(toastMessages.changePassword.error);
    }
  };

  /** Render **/

  return (
    <EditPage
      modelName="Change Password"
      modelType={DataModelType.User}
      editIcon={<VpnKey />}
      onEdit={changeUserPassword}
    >
      <FuseInput
        id="currentPassword"
        label="Current Password"
        value={formValues.currentPassword}
        updateValue={updateFormValue('currentPassword')}
        error={formErrors.currentPassword}
        type="password"
        required
      />

      <FuseInput
        id="newPassword"
        label="New Password"
        value={formValues.newPassword}
        updateValue={updateFormValue('newPassword')}
        error={formErrors.newPassword}
        type="password"
        required
      />

      <FuseInput
        id="verifyPassword"
        label="Verify Password"
        value={formValues.verifyPassword}
        updateValue={updateFormValue('verifyPassword')}
        error={formErrors.verifyPassword}
        type="password"
        required
      />
    </EditPage>
  );
});
